/**
 * Offer Details Types for Contract Term Cost Collection
 * Used in the new OfferDetailsStep between Template and Overview
 */

import type { SiteCostBreakdown } from './calculation';

/**
 * Cost breakdown data for a specific contract term (simplified - one row per contract term)
 */
export interface ContractTermBreakdown {
  energy: number; // Energy costs
  environment: number; // Environmental charges (LRET, VEEC, SRES, etc.)
  network: number; // Network/distribution charges
  others: number; // Other miscellaneous charges
  total: number; // Auto-calculated total (energy + environment + network + others)
  perYear: number; // Auto-calculated annual equivalent (total * 12)
  isCompleted?: boolean; // Track if this form has been touched/completed by user
  // Per-site rate/line-item detail from the calc engine — needed by the document
  // generator's "Site Cost Analysis" block and the Peak/Off Peak/Shoulder rate
  // columns in "List of All Offers". Must be preserved end-to-end (not stripped)
  // when this breakdown is saved to offer_cost_breakdown.cost_data.
  sites?: SiteCostBreakdown[];
  /**
   * Whole-of-term multiplier for the ANNUAL figures in sites[], as computed by the calculation
   * engine from the term's own quoted date range. Read it with contractWeightOf(); recomputing
   * from the month count discards the odd-days tail on any term not sold in whole years.
   */
  term_weight?: number;
}

/**
 * Complete cost data for a single offer across all its contract terms
 */
export interface OfferCostData {
  offerId: string; // Reference to the original offer
  offerName: string; // Display name (retailer name)
  retailerId: string; // Retailer identifier
  contractTerms: number[]; // Available contract lengths [12, 24, 36, 48]
  contractBreakdowns: {
    [termMonths: number]: ContractTermBreakdown; // Key is contract length, value is single breakdown
  };
  isComplete: boolean; // Whether all required data has been entered
  lastUpdated: Date; // Timestamp of last modification
}

/**
 * Complete offer details data for the new step
 */
export interface OfferDetailsData {
  offerCostData: OfferCostData[]; // Array of all offers with their cost data
  isStepComplete: boolean; // Whether the entire step is complete
  completionPercentage: number; // 0-100 percentage of completion
}

/**
 * Validation result for offer details data
 */
export interface OfferDetailsValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  missingData: {
    offerId: string;
    offerName: string;
    missingTerms: number[];
    missingMonths: number[];
  }[];
}

/**
 * Helper interface for bulk operations
 */
export interface BulkOperationData {
  sourceMonth: number;
  targetMonths: number[];
  operation: 'copy' | 'pattern' | 'clear';
  pattern?: {
    type: 'linear' | 'seasonal' | 'custom';
    parameters: Record<string, number>;
  };
}

/**
 * Display configuration for the offer details table
 */
export interface OfferDetailsDisplayConfig {
  showPerYear: boolean;
  showTotals: boolean;
  enableBulkOperations: boolean;
  defaultContractTerm: number;
  currencyFormat: 'AUD' | 'USD';
  decimalPlaces: number;
}

/**
 * Default values and constants
 */
export const DEFAULT_CONTRACT_BREAKDOWN: ContractTermBreakdown = {
  energy: 0,
  environment: 0,
  network: 0,
  others: 0,
  total: 0,
  perYear: 0,
};

export const AVAILABLE_CONTRACT_TERMS = [12, 24, 36, 48, 60] as const;
export type ContractTerm = typeof AVAILABLE_CONTRACT_TERMS[number];

export const COST_CATEGORIES = [
  { key: 'energy', label: 'Energy', description: 'Base energy costs for entire contract period', unit: '$' },
  { key: 'environment', label: 'Environment', description: 'Environmental charges (LRET, VEEC, SRES) for entire contract period', unit: '$' },
  { key: 'network', label: 'Network', description: 'Network and distribution charges for entire contract period', unit: '$' },
  { key: 'others', label: 'Others', description: 'Other miscellaneous charges for entire contract period', unit: '$' },
  { key: 'total', label: 'Total', description: 'Total cost for entire contract period', unit: '$', readonly: true },
  { key: 'perYear', label: 'Per Year', description: 'Annual cost (Total ÷ contract years)', unit: '$', readonly: true },
] as const;

/**
 * Utility functions
 */

/**
 * Calculate total and per year values for a contract term breakdown
 */
export const calculateContractTermTotals = (
  breakdown: Omit<ContractTermBreakdown, 'total' | 'perYear'>,
  contractTermMonths: number
): ContractTermBreakdown => {
  const total = breakdown.energy + breakdown.environment + breakdown.network + breakdown.others;
  const contractYears = contractTermMonths / 12;
  const perYear = total / contractYears; // Per Year = Total cost divided by contract years

  return {
    ...breakdown,
    total,
    perYear,
  };
};

/**
 * Create default contract term data
 */
export const createDefaultContractData = (): ContractTermBreakdown => {
  return { ...DEFAULT_CONTRACT_BREAKDOWN };
};

/**
 * Validate offer cost data
 */
export const validateOfferCostData = (data: OfferCostData): OfferDetailsValidation => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const missingData: OfferDetailsValidation['missingData'] = [];

  // Check if offer has any contract terms.
  // contractTerms is optional-chained throughout: offerCostData entries can come straight
  // from unvalidated tender.workflow_data JSONB / sessionStorage, so an entry missing
  // contractTerms would otherwise throw here and take out the whole step.
  if (!data.contractTerms?.length) {
    errors.push(`Offer ${data.offerName} has no contract terms defined`);
  }

  // Check each contract term
  (data.contractTerms || []).forEach(term => {
    const contractData = data.contractBreakdowns[term];
    if (!contractData) {
      missingData.push({
        offerId: data.offerId,
        offerName: data.offerName,
        missingTerms: [term],
        missingMonths: [],
      });
      return;
    }

    // Check for zero values (might be intentional, so warning not error)
    if (contractData.energy === 0 && contractData.environment === 0 && contractData.network === 0 && contractData.others === 0) {
      warnings.push(`Offer ${data.offerName}, ${term}-month contract: All cost categories are zero`);
    }
  });

  return {
    isValid: errors.length === 0 && missingData.length === 0,
    errors,
    warnings,
    missingData,
  };
};

/**
 * Calculate completion percentage for offer details data
 */
export const calculateCompletionPercentage = (data: OfferDetailsData): number => {
  if (data.offerCostData.length === 0) return 0;

  let totalRequired = 0;
  let totalCompleted = 0;

  // Both levels defaulted: this runs automatically on tender resume via
  // initializeOfferDetailsData, against possibly-unvalidated restored workflow data.
  (data.offerCostData || []).forEach(offer => {
    (offer.contractTerms || []).forEach(term => {
      totalRequired += 1; // One entry per contract term

      const contractData = offer.contractBreakdowns[term];
      // Consider a form complete if it has been marked as completed
      if (contractData && contractData.isCompleted) {
        totalCompleted += 1;
      }
    });
  });

  return totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 0;
};
